import { useId, useState, type FormEvent } from 'react'
import { CheckIcon } from './Icon'
import { isValidEmail, subscribe } from '../lib/subscribe'
import s from './SubscribeForm.module.css'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const INVALID_MESSAGE_WIDE = "Hmm, that doesn't look like an email — mind checking it?"
const INVALID_MESSAGE_NARROW = "That doesn't look like an email — mind checking it?"

export function SubscribeForm() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  // `null` = no error. A string is a server/network message; `invalid` is the
  // client-side validation failure, which has per-breakpoint copy.
  const [error, setError] = useState<string | 'invalid' | null>(null)
  const [confirmedEmail, setConfirmedEmail] = useState('')

  const inputId = useId()
  const errorId = useId()
  const helperId = useId()

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (status === 'submitting') return

    // Validation runs on submit only, never while typing.
    if (!isValidEmail(email)) {
      setError('invalid')
      setStatus('error')
      return
    }

    setStatus('submitting')
    setError(null)

    const result = await subscribe(email)

    if (result.ok) {
      setConfirmedEmail(email.trim())
      setStatus('success')
    } else {
      setError(result.error)
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <div className={s.success} role="status">
        <span className={s.successMark}>
          <CheckIcon />
        </span>
        <div>
          <div className={s.successTitle}>YOU'RE ON THE LIST.</div>
          <div className={s.successBody}>
            <span className={s.wide}>
              We'll send your invite to {confirmedEmail} the moment we open the doors.
            </span>
            <span className={s.narrow}>Invite heading to {confirmedEmail} soon.</span>
          </div>
        </div>
      </div>
    )
  }

  const submitting = status === 'submitting'
  const showError = error !== null

  return (
    <form className={s.wrap} onSubmit={handleSubmit} noValidate>
      <div className={s.row}>
        <input
          id={inputId}
          className={s.input}
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
            // Error clears on the next keystroke.
            if (error !== null) {
              setError(null)
              setStatus('idle')
            }
          }}
          placeholder="you@email.com"
          aria-label="Email address"
          aria-describedby={showError ? `${errorId} ${helperId}` : helperId}
          aria-invalid={showError || undefined}
          autoComplete="email"
          disabled={submitting}
        />
        <button className={s.button} type="submit" disabled={submitting}>
          {submitting ? 'SENDING…' : 'JOIN WAITLIST'}
        </button>
      </div>

      {showError && (
        <div className={s.error} id={errorId} role="alert">
          {error === 'invalid' ? (
            <>
              <span className={s.wide}>{INVALID_MESSAGE_WIDE}</span>
              <span className={s.narrow}>{INVALID_MESSAGE_NARROW}</span>
            </>
          ) : (
            error
          )}
        </div>
      )}

      <div className={s.helper} id={helperId}>
        <span className={s.wide}>We'll send your invite the moment we launch. Nearly there.</span>
        <span className={s.narrow}>Your invite, the moment we launch.</span>
      </div>
    </form>
  )
}
